import {
    Box,
    ListItemIcon,
    ListItemText,
    MenuItem,
    type MenuItemProps,
} from '@mui/material';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { forwardRef, type KeyboardEvent, type ReactNode } from 'react';
import MenuRowLead from './MenuRowLead';
import { ICON_PX } from '~/theme/iconSizes';

/**
 * A menu row that opens a flyout: either the current choice, named after its
 * dataset chip, which the flyout changes, or an action after its icon, which
 * the flyout carries out. The flyout is a menu, or a dialog for content a
 * menu cannot hold. A click toggles the flyout and Right Arrow opens it.
 * While the flyout is open the row is filled and its chevron turns teal.
 *
 * The other MenuItem props pass through: a Menu steers focus through its
 * direct children's props, so the row stays reachable from the keyboard.
 */
const MenuFlyoutRow = forwardRef<
    HTMLLIElement,
    Omit<
        MenuItemProps,
        'onClick' | 'onKeyDown' | 'selected' | 'children' | 'sx' | 'ref'
    > & {
        /** The dataset chip of a choice row. */
        leading?: ReactNode;
        /** The icon of an action row, in place of a chip. */
        Icon?: LucideIcon;
        name: ReactNode;
        /** Whether this row's flyout is open. */
        open: boolean;
        onToggle: () => void;
        onOpen: () => void;
        /** Draws the name in the primary colour, for an offer to load one. */
        offer?: boolean;
        /** The kind of flyout the row opens. */
        popup?: 'menu' | 'dialog';
    }
>(function MenuFlyoutRow(
    {
        leading,
        Icon,
        name,
        open,
        onToggle,
        onOpen,
        offer = false,
        popup = 'menu',
        ...menuItemProps
    },
    ref
) {
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            onOpen();
        }
    };
    return (
        <MenuItem
            {...menuItemProps}
            ref={ref}
            selected={open}
            onClick={onToggle}
            onKeyDown={handleKeyDown}
            aria-haspopup={popup}
            aria-expanded={open}
        >
            {Icon ? (
                <ListItemIcon sx={{ width: ICON_PX, justifyContent: 'center' }}>
                    <Icon />
                </ListItemIcon>
            ) : (
                <MenuRowLead chip={leading} />
            )}
            <ListItemText
                primaryTypographyProps={{
                    noWrap: true,
                    // A choice names a dataset; an action reads as one.
                    fontWeight: Icon ? undefined : 500,
                    color: offer ? 'primary.main' : undefined,
                }}
            >
                {name}
            </ListItemText>
            <Box
                display="flex"
                flexShrink={0}
                color={open ? 'primary.main' : 'text.secondary'}
                sx={(theme) => ({
                    transition: theme.transitions.create('color', {
                        duration: theme.transitions.duration.short,
                    }),
                })}
            >
                <ChevronRight size={ICON_PX} />
            </Box>
        </MenuItem>
    );
});

export default MenuFlyoutRow;
